import { Controller } from "@hotwired/stimulus"

// Discloses the full text of a line-clamped block in a popover: on hover/focus, pinned
// open by a click so it also works on touch and stays up while it's being read.
//
// The popover is suppressed whenever the text already fits, so a short value doesn't
// sprout a box repeating what is already on screen. That is measured again at open time
// rather than only on connect, since a resize changes what overflows.
export default class extends Controller {
  static targets = ["popover", "trigger"]

  connect() {
    this.pinned = false
    this.reposition = this.reposition.bind(this)
    this.markTruncated()
    // Web fonts can land after connect and change what overflows.
    document.fonts?.ready.then(() => this.markTruncated())
  }

  disconnect() {
    this.stopTracking()
  }

  dismiss(event) {
    if (this.element.contains(event.target)) return
    this.pinned = false
    this.close()
  }

  hide() {
    if (!this.pinned) this.close()
  }

  show() {
    this.open()
  }

  toggle() {
    if (!this.truncated) return
    this.pinned = !this.pinned
    this.pinned ? this.open() : this.close()
  }

  open() {
    if (!this.truncated) return

    this.popoverTarget.classList.remove("hidden")
    this.reposition()
    // Capture phase so scrolling inside any container is caught.
    window.addEventListener("scroll", this.reposition, true)
    window.addEventListener("resize", this.reposition)
  }

  close() {
    this.popoverTarget.classList.add("hidden")
    this.stopTracking()
  }

  // Keyboard users can only reach text that has something more to show.
  markTruncated() {
    if (this.truncated) {
      this.triggerTarget.tabIndex = 0
      this.triggerTarget.classList.add("cursor-help")
    } else {
      this.triggerTarget.removeAttribute("tabindex")
      this.triggerTarget.classList.remove("cursor-help")
    }
  }

  reposition() {
    const rect = this.triggerTarget.getBoundingClientRect()
    const { offsetHeight: height, offsetWidth: width } = this.popoverTarget
    // Below the text, unless that would run off the bottom of the viewport.
    const below = rect.bottom + 6
    const fitsBelow = below + height <= window.innerHeight - 8

    Object.assign(this.popoverTarget.style, {
      left: `${Math.min(Math.max(8, rect.left), window.innerWidth - width - 8)}px`,
      margin: "0",
      position: "fixed",
      top: `${fitsBelow ? below : Math.max(8, rect.top - height - 6)}px`,
    })
  }

  stopTracking() {
    window.removeEventListener("scroll", this.reposition, true)
    window.removeEventListener("resize", this.reposition)
  }

  get truncated() {
    // A rounded line height can leave a pixel of slack on text that fits exactly.
    return this.triggerTarget.scrollHeight > this.triggerTarget.clientHeight + 1
  }
}
